import React from 'react';

class ErrorBoundary extends React.Component {
  constructor(props) {
    super(props);
    this.state = { 
      hasError: false,
      error: null,
      errorInfo: null
    };
  }

  static getDerivedStateFromError(error) {
    // Update state so the next render will show the fallback UI
    return { hasError: true };
  }

  componentDidCatch(error, errorInfo) {
    // Log error details (in production, send to monitoring service)
    console.error('Error Boundary caught an error:', error, errorInfo);
    
    this.setState({
      error,
      errorInfo
    });

    // In production, send to error monitoring service (e.g., Sentry)
    // Sentry.captureException(error, { extra: errorInfo });
  }

  handleReset = () => {
    this.setState({ hasError: false, error: null, errorInfo: null });
    window.location.href = '/#/dashboard';
  };

  handleRefresh = () => {
    window.location.reload();
  };

  render() {
    if (this.state.hasError) {
      // Custom error UI
      return (
        <div className="min-h-screen flex items-center justify-center bg-gradient-to-br from-red-600 via-orange-600 to-yellow-500">
          <div className="text-center px-4">
            {/* Error Icon */}
            <div className="mb-8">
              <div className="w-64 h-64 mx-auto relative">
                <div className="absolute inset-0 flex items-center justify-center">
                  <div className="animate-pulse">
                    <svg 
                      className="w-32 h-32 text-white" 
                      fill="none" 
                      stroke="currentColor" 
                      viewBox="0 0 24 24"
                    >
                      <path 
                        strokeLinecap="round" 
                        strokeLinejoin="round" 
                        strokeWidth={2} 
                        d="M12 9v2m0 4h.01m-6.938 4h13.856c1.54 0 2.502-1.667 1.732-3L13.732 4c-.77-1.333-2.694-1.333-3.464 0L3.34 16c-.77 1.333.192 3 1.732 3z" 
                      />
                    </svg>
                  </div>
                </div>
              </div>
            </div>

            {/* Error Message */}
            <div className="bg-white/10 backdrop-blur-lg rounded-2xl p-8 max-w-md mx-auto border border-white/20 shadow-2xl">
              <h2 className="text-3xl font-bold text-white mb-4">
                Oops! Something Went Wrong
              </h2>
              <p className="text-white/90 text-lg mb-6">
                We're sorry, but something unexpected happened. Our team has been notified.
              </p>

              {/* BizHub Branding */}
              <div className="mb-6">
                <p className="text-white/80 text-sm">
                  <span className="font-bold text-yellow-300">BizHub</span> - Let's Talk Strategy
                </p>
              </div>

              {/* Action Buttons */}
              <div className="space-y-4">
                <button
                  onClick={this.handleRefresh}
                  className="w-full bg-white hover:bg-gray-100 text-gray-900 font-bold py-3 px-6 rounded-lg transition-all duration-200 transform hover:scale-105 shadow-lg"
                >
                  Refresh Page
                </button>
                
                <button
                  onClick={this.handleReset}
                  className="w-full bg-yellow-400 hover:bg-yellow-500 text-gray-900 font-bold py-3 px-6 rounded-lg transition-all duration-200 transform hover:scale-105 shadow-lg"
                >
                  Go to Dashboard
                </button>
              </div>

              {/* Error details for development (hidden in production) */}
              {process.env.NODE_ENV === 'development' && this.state.error && (
                <details className="mt-6 text-left">
                  <summary className="cursor-pointer text-white/70 text-sm hover:text-white">
                    Error Details (Dev Only)
                  </summary>
                  <div className="mt-2 p-4 bg-black/20 rounded-lg text-xs text-white/80 overflow-auto max-h-40">
                    <p className="font-mono">{this.state.error.toString()}</p>
                    {this.state.errorInfo && (
                      <pre className="mt-2 font-mono text-xs">
                        {this.state.errorInfo.componentStack}
                      </pre>
                    )}
                  </div>
                </details>
              )}
            </div>

            {/* Support Link */}
            <div className="mt-8 text-white/80 text-sm">
              <p>
                Need help?{' '}
                <a 
                  href="mailto:support@letstalkstrategy.io" 
                  className="text-yellow-300 hover:text-yellow-400 underline"
                >
                  Contact Support
                </a>
              </p>
            </div>
          </div>
        </div>
      );
    }

    return this.props.children;
  }
}

export default ErrorBoundary;
